import { CustomValidators } from "src/app/classes/custom-validators";
import { PracticePathUtil } from "../../../../services/practice-path-util";
import { CriticalPoint, CriticalPointAssignment, CriticalPointPositionInPath, CriticalPointTemplate, PathItem, StopPoint } from "./../../../../classes/model/practice-path";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Injector,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NotifierService } from 'src/app/services/common/notifier-service.service';
import { GeoHelper } from "../../GeoHelper";
import { BehaviorSubject, Subscription } from "rxjs";
import { OnDestroy } from "@angular/core";
import { VideoPlayerController } from "src/app/modules/shared-module/components/video-player/video-player.controller";
import { VideoPlayerComponent } from "src/app/modules/shared-module/components/video-player/video-player.component";
import { OtherPracticePathsToCriticalPointAssignedToDialogComponent, OtherPracticePathsToCriticalPointAssignedToDialogData } from "./other-practice-paths-to-critical-point-assigned-to-dialog/other-practice-paths-to-critical-point-assigned-to-dialog.component";
import { PracticePathGlobalVideoEditorPageService } from "../../services/practice-path-global-video-editor-page.service";
import { CriticalPointService } from "src/app/services/critical-point-service";
import { Permission } from "src/app/classes/model/permissions";
import { PermissionService } from "src/app/services/common/permission.service";
import { PopoverComponent } from "@ngx-popovers/popover";
import { CriticalPointTemplateSelectorService } from "src/app/modules/critical-point-template/services/critical-point-template-selector.service";
import { CriticalPointAiPopoverStateService } from "src/app/services/critical-point-ai-popover-state.service";

// A kritikus pont szerkesztőnek az eredménye
export type CriticalPointEditorResult = {
  title: string;
  description: string;
  isLocal: boolean;
  hasDirectionalAngle: boolean;
  criticalPointCoordinate: { latitude: number; longitude: number };
  anchorPoint: { latitude: number; longitude: number };
  directionAngle?: {
    point: { latitude: number; longitude: number };
    angle: number;
  };
  stopPoints: {
    stopPointUuid: string;
    focusSettings?: { x: number; y: number; radius: number };
    stopTimeInMs: number;
    isActive: boolean;
  }[];
};

export type CriticalPointEditorNavigationDirection = "previous" | "next";

export type CriticalPointEditorNavigationCommand = {
  type: "navigate";
  direction: CriticalPointEditorNavigationDirection;
};

export type CriticalPointEditorDialogClosePayload =
  | CriticalPointEditorResult
  | CriticalPointEditorNavigationCommand;

@Component({
  selector: "app-critical-point-editor-dialog",
  templateUrl: "./critical-point-editor-dialog.component.html",
  styleUrls: ["./critical-point-editor-dialog.component.scss"],
})
export class CriticalPointEditorDialogComponent
  implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("descriptionTextArea", { read: ElementRef })
  descriptionTextArea: ElementRef;

  @ViewChild(VideoPlayerComponent)
  videoPlayerComponent!: VideoPlayerComponent;

  popoverBindTo: HTMLElement | string = ".mat-mdc-dialog-container";

  readonly maxTitleLength = 28;
  // A kritikus pontot ekkora sugarú körön belül lehet mozgatni a lerakásától
  readonly criticalPointAnchorRadius = 100;
  readonly focusHorizontalPadding = 0.15;

  // Kritikus ponthoz tartozó adatok
  formGroupForCriticalPoint!: FormGroup;

  videoPlayerPositionSubscription: Subscription;
  previewVideoPlayerController!: VideoPlayerController;

  // Fókusz pont szerkesztés aktív-e jelenleg (ha igen, akkor a mouse eventeket lekezeljük)
  isFocusEditState: boolean = false;

  // Jelenlegi irányszög
  currentDirectionalAngle?: {
    angle: number;
    point: { latitude: number; longitude: number };
  };

  isFocusedFormControllers: FormControl[] = [];
  selectedStopPointUuid = "";

  // Ha a stop pontok változnak ezen keresztül értesítsjük a térképet
  stopPointsAsObservable: BehaviorSubject<StopPoint[]> = new BehaviorSubject(
    []
  );

  criticalPointPositionInPaths: Array<CriticalPointPositionInPath> | null = null;
  criticalPointPositionInPathsCalculationHasError: boolean = false;

  private readonly stopPointNearTimeThresholdInMs: number = 500;
  private readonly disableStopPointPlacingInBeginningInMs: number = 1000;

  constructor(
    public dialog: MatDialogRef<CriticalPointEditorDialogComponent>,
    private dialogService: MatDialog,
    private notifierService: NotifierService,
    private practicePathGlobalVideoEditorPageService: PracticePathGlobalVideoEditorPageService,
    private practiceEditorService: PracticePathUtil,
    private viewContainerRef: ViewContainerRef,
    private injector: Injector,
    private criticalPointService: CriticalPointService,
    private permissionService: PermissionService,
    private criticalPointTemplateSelectorService: CriticalPointTemplateSelectorService,
    private aiPopoverStateService: CriticalPointAiPopoverStateService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      // az összes többi kritikus pont
      // megjelenítjük,hogy a jelenlegi kritikus pontot a többihez képest mozgathassuk ha kell (ne legyenek egymáson)
      allOtherCriticalPoints: CriticalPoint[];
      criticalPoint: CriticalPoint;
      videoUrl: string;
      defaultSelectedStopPointIndex: number;
      geoTaggedFrames: PathItem[];
      stopPoints: StopPoint[];
      showPathMarkers: boolean; // Sárga pöttyöket megjelenítsük-e ,vagy csak a polyline-t
      actualPracticePathUuid: string;
    }
  ) {
    // Default kiválasztott stop point beállítása
    if (
      this.data.stopPoints.length != 0 &&
      this.data.defaultSelectedStopPointIndex != undefined
    ) {
      this.selectedStopPointUuid =
        this.data.stopPoints[
          this.data.defaultSelectedStopPointIndex
        ].stopPointUuid;
    }

    // Ha van irányszög az init adatban beállítjuk!
    if (this.data.criticalPoint.directionalAngle != undefined) {
      this.currentDirectionalAngle = {
        angle: this.data.criticalPoint.directionalAngle,
        point: this.data.criticalPoint.directionalAnglePoint,
      };
    }
    // Az aktív stop pontokat frissítjük, ezt kapja meg a directional angle
    // ha a stop pontok aktivitása változik azt ezen a BehaviourSubject-en megkapja
    this.stopPointsAsObservable.next(this.getActiveStopPoints());

    const overlayPane = (this.viewContainerRef.element?.nativeElement as HTMLElement | undefined)?.closest?.(
      ".cdk-overlay-pane"
    ) as HTMLElement | null;
    if (overlayPane) {
      this.popoverBindTo = overlayPane;
    }
  }
  ngOnDestroy(): void {
    this.stopPointsAsObservable.unsubscribe();
    this.formControllerChangeSubscriptions.forEach((s) => s.unsubscribe());
    this.formControllerChangeSubscriptions = [];
    this.isFocusedFormControllers = [];

    this.videoPlayerPositionSubscription?.unsubscribe();
    this.previewVideoPlayerController?.destroy();

    // Clear AI popover state when dialog closes
    this.aiPopoverStateService.clearState();
  }

  ngOnInit(): void {
    this.initFormGroupForCriticalPoint();
    this.initIsFocusedFormControllers();
    this.clampStopPointFocusCenters();

    // Ha nem létezik még megállási pont ehhez a kritikus ponthoz
    // akkor próbáljunk meg becsülni. Lehet, hogy becslés után se lesz megállási pont
    // ha az adott útvonaltól távol esik
    if (this.data.stopPoints.length == 0) {
      this.onTapStopPointsReset();
      if (this.data.stopPoints.length != 0) {
        // Ha a resetelés hatására lett legalább 1, akkor állítsunk be default indexet
        // hogy a videót az első becslésre tekerjük
        this.data.defaultSelectedStopPointIndex = 0;
      }
    }

    this.previewVideoPlayerController = new VideoPlayerController(
      this.data.videoUrl,
      this.getInitialVideoPosition()
    );

    // Check if the critical point has a uuid to determine if we adding a new critical point or
    // editing an existing one
    if (this.data.criticalPoint.uuid) {
      // Init the other practice path to critical point is assigned to fields only if we are
      // in editing mode
      this.initOtherPracticePathsToAssignedTo();
    }
  }

  ngAfterViewInit(): void {
    this.addScrollbarCheckForDescriptionFC();
  }

  /**
   * Inits the `otherPracticePathsToAssignedTo` array with practice paths that has the actually editet critical
   * point assigned and differs from the actual edited practice path. If the data fetching was unsuccessful
   * the `otherPracticePathsLoadingHasError` to `true`.
   */
  protected async initOtherPracticePathsToAssignedTo(): Promise<void> {
    // Set the error flag to it's default state
    this.criticalPointPositionInPathsCalculationHasError = false;

    try {
      // Fetch the critical point's position in the practice paths
      this.criticalPointPositionInPaths = await this.criticalPointService.fetchCalculatedPositionOfCriticalPointInAssignedPaths(
        this.practicePathGlobalVideoEditorPageService.city.uuid,
        this.data.criticalPoint.uuid
      );
      // Filter out the actually edited practice path
      this.criticalPointPositionInPaths = this.criticalPointPositionInPaths.filter(
        (criticalPointPositionInPath: CriticalPointPositionInPath) => {
          return criticalPointPositionInPath.practicePathUuid !== this.data.actualPracticePathUuid
        }
      );
    } catch (error: any) {
      console.error(error);
      // Set the error flag that an error happened during the fetching
      this.criticalPointPositionInPathsCalculationHasError = true;
    }
  }

  addScrollbarCheckForDescriptionFC() {
    // scrollbar check validators utólag
    // mivel szükség van a text area view-ra ezért afterViewInit-ben kell hozzáadnunk a validatort
    // a text area még ngOnInit-ben nem létezik

    const descFC = this.formGroupForCriticalPoint.get("descriptionFC");
    descFC.addValidators(
      CustomValidators.noScrollbar(this.descriptionTextArea.nativeElement)
    );

    this.formGroupForCriticalPoint
      .get("descriptionFC")
      .valueChanges.subscribe(() => {
        // Maximum 8 sort engedélyezünk, ha több, akkor töröljük az utoljára leütött karaktereket
        const hasScrollbarErros = CustomValidators.noScrollbar(
          this.descriptionTextArea.nativeElement
        )(descFC);
        if (hasScrollbarErros != null) {
          descFC.setValue(descFC.value.slice(0, -1));
        }
      });
  }

  public onAiDescriptionAccept(
    newDescription: string,
    popover: PopoverComponent
  ): void {
    const descriptionControl = this.formGroupForCriticalPoint.get("descriptionFC");
    if (descriptionControl) {
      descriptionControl.setValue(newDescription ?? "");
      descriptionControl.markAsDirty();
      descriptionControl.markAsTouched();
    }

    popover.close();
  }

  formControllerChangeSubscriptions: Subscription[] = [];
  // A fókusz checkboxokat inicializálja
  // Ha például resetelődnek a megállási pontok,vagy törlődik egy, stb..
  // Ha már egy hozzáadott megállási pontnak egy attribútuma változik akkor NEM kell meghívni
  initIsFocusedFormControllers() {
    this.isFocusedFormControllers = [];

    // Az előzől feliratkozásokat töröljük ha voltak
    this.formControllerChangeSubscriptions.forEach((f) => f.unsubscribe());
    this.formControllerChangeSubscriptions = [];

    for (let i = 0; i < this.data.stopPoints.length; i++) {
      const newFc = new FormControl(
        this.data.stopPoints[i].focusSettings != undefined
      );
      this.isFocusedFormControllers.push(newFc);

      this.formControllerChangeSubscriptions.push(
        newFc.valueChanges.subscribe((newValue) => {
          if (newValue) {
            this.isFocusEditState = true;
            if (
              this.selectedStopPointUuid !=
              this.data.stopPoints[i].stopPointUuid
            ) {
              this.selectedStopPointUuid =
                this.data.stopPoints[i].stopPointUuid;
            }

            this.data.stopPoints[i].focusSettings ??= {
              radius: 0.3,
              x: 0.5,
              y: 0.5,
            };
          } else {
            if (
              this.selectedStopPointUuid ==
              this.data.stopPoints[i].stopPointUuid
            ) {
              this.isFocusEditState = false;
            }
            this.data.stopPoints[i].focusSettings = undefined;
          }
        })
      );
    }
  }

  getActiveStopPoints = () => this.data.stopPoints.filter((sp) => sp.isActive);

  // A kritikus pontnak az adatait tároló form group-ot inicializálja
  initFormGroupForCriticalPoint() {
    this.formGroupForCriticalPoint = new FormGroup({
      titleFC: new FormControl(this.data.criticalPoint.title ?? "", [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(this.maxTitleLength),
      ]),
      descriptionFC: new FormControl(
        this.data.criticalPoint.description ?? "",
        [
          Validators.required,
          Validators.minLength(1),
          CustomValidators.noNewlineCharacter,
        ]
      ),
      hasDirectionAngleFC: new FormControl(
        /* this.data.criticalPoint.directionalAngle != null */ false,
        []
      ),
      isLocalFC: new FormControl(this.data.criticalPoint.isLocal, []),
    });

    // Clear AI popover state when description changes externally
    this.formGroupForCriticalPoint
      .get("descriptionFC")
      .valueChanges.subscribe(() => {
        // Only clear if there's state and the new value differs from the saved base description
        const state = this.aiPopoverStateService.getState();
        if (state) {
          const currentDescription = this.formGroupForCriticalPoint.get("descriptionFC").value ?? "";
          if (currentDescription !== state.baseDescription) {
            this.aiPopoverStateService.clearState();
          }
        }
      });
  }

  // Ha nem találja undefined-al tér vissza
  // egyébként pedig az éppen kiválasztott megállási ponttal
  public getSelectedStopPoint(): StopPoint | undefined {
    return this.data.stopPoints.find(
      (value) => value.stopPointUuid == this.selectedStopPointUuid
    );
  }

  // be vagy kipipáltak egy isActive checkboxot
  onChangeStopPointActiveCheckbox() {
    this.stopPointsAsObservable.next(this.data.stopPoints);
  }

  // Megnézzük, hogy az adott pontra eltekerhet-e
  // akkor tekerhet el, ha az a kritikus pont körén belül marad (anchorPoint középpontú körön belül)
  private isSeekAllowed(seekTo: number): boolean {
    const geoTagged =
      this.practiceEditorService.getClosestGeoTaggedFrameToVideoPosition(
        this.data.geoTaggedFrames,
        seekTo
      );
    const distFromAnchor = GeoHelper.distance(
      geoTagged.position.latitude,
      geoTagged.position.longitude,
      this.data.criticalPoint.anchorPoint.latitude,
      this.data.criticalPoint.anchorPoint.longitude
    );
    return distFromAnchor < this.criticalPointAnchorRadius;
  }

  onTapStopPointsReset() {
    const newStopPoints = this.practiceEditorService.estimateStopPoints(
      this.data.geoTaggedFrames,
      this.data.criticalPoint.coordinate
    );
    this.data.stopPoints = newStopPoints.map((sp) => {
      return {
        stopPointUuid: Math.random().toString(), // TODO CHANge,
        stopTimeInMs: sp.stopTimeInMs,
        isActive: false,
      };
    });
    this.stopPointsAsObservable.next(this.data.stopPoints);
    if (this.data.stopPoints.length != 0) {
      this.selectedStopPointUuid = this.data.stopPoints[0].stopPointUuid;
      this.previewVideoPlayerController?.seekTo(
        this.data.stopPoints[0].stopTimeInMs
      );
    }

    this.initIsFocusedFormControllers();
  }

  getInitialVideoPosition() {
    const initialVideoPosition =
      this.data.defaultSelectedStopPointIndex == undefined
        ? 0
        : this.data.stopPoints[this.data.defaultSelectedStopPointIndex]
          .stopTimeInMs;
    return initialVideoPosition;
  }

  // Amikor a video player inicializálódott. (A video-player hívja, mint callbacket)
  videoPlayerInited() {
    const initialVideoPosition = this.getInitialVideoPosition();

    let lastVideoPosition = initialVideoPosition;
    this.previewVideoPlayerController._currentPositionInMs$.next(
      initialVideoPosition
    );

    // Ha a videó pozíció változik, akkor frissítsük a jelenleg kiválasztott megállási pontot
    let lastWarningTimestamp = 0;

    this.videoPlayerPositionSubscription = this.previewVideoPlayerController
      .getCurrentPositionInMs$()
      .subscribe((currentPos) => {
        const selectedStopPoint = this.getSelectedStopPoint();
        // Ha nincs kiválasztva megállási pont (mert például nem létezik egy se)
        // akkor nem kell semmit updatelni
        if (!selectedStopPoint) return;

        if (this.isSeekAllowed(currentPos)) {
          selectedStopPoint.stopTimeInMs = currentPos;

          // If the stop point was active
          if (selectedStopPoint.isActive && this.isStoppingTimeInvalid(selectedStopPoint)) {
            selectedStopPoint.isActive = false;
          }

          lastVideoPosition = currentPos;
        } else {
          // Ha már legalább 6 másodperce volt az utolsó figyelmeztetés
          if (lastWarningTimestamp < Date.now().valueOf() - 6000) {
            this.notifierService.notify(
              "error",
              "Nem tekerhetsz ki a zöld körön kívülre!"
            );
            lastWarningTimestamp = Date.now().valueOf();
          }
          this.previewVideoPlayerController.seekTo(lastVideoPosition);
        }

        this.stopPointsAsObservable.next(this.data.stopPoints);
      });
  }

  // Az input alapján összeállítja a kritikus pontot
  // amivel a dialógus visszatérhet
  private composeCriticalPointFromInputs(): CriticalPointEditorResult {
    this.clampStopPointFocusCenters();

    const hasDirectionAngle = this.formGroupForCriticalPoint.get(
      "hasDirectionAngleFC"
    )!.value;
    return {
      description: this.formGroupForCriticalPoint.get("descriptionFC")!.value,
      title: this.formGroupForCriticalPoint.get("titleFC")!.value,
      directionAngle: hasDirectionAngle
        ? this.currentDirectionalAngle
        : undefined,
      hasDirectionalAngle: hasDirectionAngle,
      isLocal: this.formGroupForCriticalPoint.get("isLocalFC")!.value,
      stopPoints: this.data.stopPoints,
      criticalPointCoordinate: this.data.criticalPoint.coordinate,
      anchorPoint: this.data.criticalPoint.anchorPoint,
    };
  }

  // Kritikus pont mentése
  onTapSaveButton() {
    this.trySaveCriticalPoint();
  }

  // Ha nem volt fókusz mód és bekapcsoljuk akkor az edit alapból legyen true!
  onTapEditFocus() {
    this.isFocusEditState = !this.isFocusEditState;
  }

  // Bezárja a dialógust
  onTapCancelButton() {
    this.dialog.close();
  }

  // A stopPointUuid-ra kattintott (A listában a span text)
  onTapStopPoint(stopPointUuid: string) {
    this.selectedStopPointUuid = stopPointUuid;
    const point = this.data.stopPoints.find(
      (value) => value.stopPointUuid == stopPointUuid
    );
    this.previewVideoPlayerController.seekTo(point?.stopTimeInMs ?? 0);

    // Állítsuk be a fókusz checkboxot
    this.isFocusEditState = false;
  }

  // Görgő segítségével a kritikus pont sugarának változtatása
  @HostListener("pinch", ["$event"])
  @HostListener("wheel", ["$event"])
  onWheelScroll(event: WheelEvent | Event) {
    if (this.isFocusEditState == false) return;

    // Only WheelEvent has deltaY - ignore other event types
    if (!("deltaY" in event)) return;

    const scaleExtent = event.deltaY / 2500;
    this.getSelectedStopPoint()!.focusSettings!.radius += scaleExtent;

    this.getSelectedStopPoint()!.focusSettings!.radius = Math.max(
      0.2,
      Math.min(0.7, this.getSelectedStopPoint()!.focusSettings!.radius)
    );
  }

  // Kritikus pont mozgatása egérrel
  @HostListener("document:mousemove", ["$event"])
  onMouseMove(e: any) {
    if (this.isFocusEditState == false) return;
    // A focus overlay-en belül a canvas azonosítója 'focus'
    if (e.target.id == "focus") {
      this.getSelectedStopPoint()!.focusSettings!.x =
        this.clampFocusCenterX(e.layerX / e.target.clientWidth);
      this.getSelectedStopPoint()!.focusSettings!.y =
        e.layerY / e.target.clientHeight;
    }
  }

  // A fókusz pont pozícióját véglegesíteni egy bal klikkel lehet
  // ez a listener kezeli le a canvas-on a kattintást (aminek az id-ja focus)
  // és a focus szerkesztést kikapcsolja (ezzel véglegesítve azt a pozíciót ahol az egér éppen van)
  @HostListener("click", ["$event"])
  onClick(e: any) {
    if (this.isFocusEditState == false) return;

    if (e.target.id == "focus") {
      this.isFocusEditState = false;
    }
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent) {
    if (this.isSaveShortcut(event)) {
      event.preventDefault();
      event.stopPropagation();
      this.trySaveCriticalPoint(true);
      return;
    }

    if (this.shouldIgnoreKeyboardNavigation(event)) {
      return;
    }

    if (event.key === "í" || event.key === "Í") {
      event.preventDefault();
      this.emitNavigationCommand("previous");
      return;
    }

    if (event.key === "y" || event.key === "Y") {
      event.preventDefault();
      this.emitNavigationCommand("next");
    }
  }

  protected openOtherPracticePathsToCriticalPointAssignedToDialog(): void {
    this.dialogService.open<
      OtherPracticePathsToCriticalPointAssignedToDialogComponent,
      OtherPracticePathsToCriticalPointAssignedToDialogData
    >(
      OtherPracticePathsToCriticalPointAssignedToDialogComponent,
      {
        data: {
          criticalPointPositionInPaths: this.criticalPointPositionInPaths
        },
        viewContainerRef: this.viewContainerRef,
        injector: this.injector
      }
    );
  }

  protected onTemplateAutocompleteSelection(template: CriticalPointTemplate | null): void {
    if (!template) {
      return;
    }

    const titleControl = this.formGroupForCriticalPoint.get("titleFC");
    const normalizedTitle = template.title ?? "";
    if (titleControl.value !== normalizedTitle) {
      titleControl.setValue(normalizedTitle, { emitEvent: false });
    }
    titleControl.markAsDirty();
    titleControl.markAsTouched();
    titleControl.updateValueAndValidity({ emitEvent: false });


    const descriptionControl = this.formGroupForCriticalPoint.get("descriptionFC");
    descriptionControl.setValue(template.description ?? "");
    descriptionControl.markAsDirty();
    descriptionControl.markAsTouched();
    descriptionControl.updateValueAndValidity();


    // Get the isGlobal value from the template
    const isLocalFCControl = this.formGroupForCriticalPoint.get("isLocalFC");
    isLocalFCControl.setValue(!template.isGlobal, { emitEvent: false });
    isLocalFCControl.markAsDirty();
    isLocalFCControl.markAsTouched();
    isLocalFCControl.updateValueAndValidity({ emitEvent: false });


    // We have to log the template usage

  }

  protected hasUserPracticePathWritePermission(): boolean {
    return this.permissionService.isLoggedUserHasPermission(Permission.PracticePathWrite);
  }

  private trySaveCriticalPoint(shouldNotifyWhenSaveBlocked: boolean = false): void {
    if (this.canSaveCriticalPoint() === false) {
      if (shouldNotifyWhenSaveBlocked) {
        this.notifyWhyCriticalPointCannotBeSaved();
      }
      return;
    }

    // Állítsuk össze az eredmény objektumot
    const composedPointFromInputs = this.composeCriticalPointFromInputs();
    this.dialog.close(composedPointFromInputs);
  }

  /**
   * Determines that the target stop point's checkbox is disabled. The checkbox should be disabled when
   * the stop point's stopping time is invalid and the checkbox is not checked.
   *
   * @param targetStopPoint the target stop point
   *
   * @returns true if the checkbox should be disabled,
   */
  protected isStoppingPointCheckDisabled(targetStopPoint: StopPoint): boolean {
    return targetStopPoint.isActive === false && this.isStoppingTimeInvalid(targetStopPoint);
  }

  /**
   * Determines if the target stop point's stopping time is valid. The time is invalid if the stop point's
   * stopping time is too close to the beginning of the video or too close to an other stop point's stopping
   * time.
   *
   * @param targetStopPoint the target stop point
   *
   * @returns true if the stopping time is invalid, false otherwise
   */
  private isStoppingTimeInvalid(targetStopPoint: StopPoint): boolean {
    return this.hasAnotherStoppingPointNearInTime(targetStopPoint) ||
      targetStopPoint.stopTimeInMs < this.disableStopPointPlacingInBeginningInMs;
  }

  /**
   * Gets the reason for the disabled stop point checkbox.
   *
   * @param targetStopPoint the target stop point
   *
   * @returns the reason if some of the conditions met of the disabling, null otherwise
   */
  protected getStopPointCheckDisabledMessage(targetStopPoint: StopPoint): string | null {
    if (targetStopPoint.stopTimeInMs < this.disableStopPointPlacingInBeginningInMs) {
      return "A megállási pont túl közel van a videó elejéhez!";
    }

    const nearStopPoint = this.getAnotherStoppingPointNearInTime(targetStopPoint);
    if (nearStopPoint) {
      // Get the index if the critical point assignment
      const criticalPointAssignmentIndex: number = this.practicePathGlobalVideoEditorPageService.criticalPointAssignments$.findIndex(
        (criticalPointAssignment: CriticalPointAssignment) => {
          return criticalPointAssignment.uuid === nearStopPoint.criticalPointAssignmentUuid;
        }
      );

      const criticalPointTitle: string = this.practicePathGlobalVideoEditorPageService.city.criticalPoints.find(
        (criticalPoint: CriticalPoint) => {
          return criticalPoint.uuid === nearStopPoint.criticalPointUuid;
        }
      )?.title ?? "Hiba a kritikus pont szöveg meghatározása közben";

      return `Túl közeli megálláspont: ${criticalPointAssignmentIndex + 1}. ${criticalPointTitle}`;
    }

    return null;
  }

  /**
   * Determines that the target stop point's stopping time is near to an other stop point's stopping time.
   *
   * @param targetStopPoint the target stop point
   *
   * @returns true if there is an other stopping time near, false otherwise
   */
  private hasAnotherStoppingPointNearInTime(targetStopPoint: StopPoint): boolean {
    return this.getAnotherStoppingPointNearInTime(targetStopPoint) != null;
  }

  /**
   * Gets the first critical point assignment's stop point which is too close to the target stop point' stoping time.
   * If no such stop point exists, `null` returned.
   *
   * @param targetStopPoint the target stop point
   *
   * @returns information object about the close stop point if one exists, `null` otherwise
   */
  private getAnotherStoppingPointNearInTime(
    targetStopPoint: StopPoint
  ): { criticalPointAssignmentUuid: string, criticalPointUuid: string, stopPointuUuid: string } | null {
    // Iterate over all critical point assingments of the path
    for (const criticalPointAssignment of this.practicePathGlobalVideoEditorPageService.criticalPointAssignments$) {
      // Iterate over the critical point's stopping points
      for (const stopPoint of criticalPointAssignment.stopPoints) {
        // If the stopping point is the same as the target stopping point or
        // the stopping point is inactive
        if (stopPoint.stopPointUuid === targetStopPoint.stopPointUuid || stopPoint.isActive === false) {
          // There is nothing to do
          continue;
        }

        // Calculate the time difference between the stop points
        const timeDifference: number = Math.abs(stopPoint.stopTimeInMs - targetStopPoint.stopTimeInMs);
        // If the time difference is less than the threshold
        if (timeDifference < this.stopPointNearTimeThresholdInMs) {
          // Return the found stop point info, because then there is a stopping point near
          return {
            criticalPointAssignmentUuid: criticalPointAssignment.uuid,
            criticalPointUuid: criticalPointAssignment.criticalPointUuid,
            stopPointuUuid: stopPoint.stopPointUuid
          };
        }
      }
    }

    // If no stopping point found near, return null
    return null;
  }

  private shouldIgnoreKeyboardNavigation(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (target == null) {
      return false;
    }

    const tagName = target.tagName;
    if (tagName === "INPUT" || tagName === "TEXTAREA") {
      return true;
    }

    return target.isContentEditable === true;
  }

  private canSaveCriticalPoint(): boolean {
    return this.formGroupForCriticalPoint?.valid === true &&
      this.hasUserPracticePathWritePermission();
  }

  private isSaveShortcut(event: KeyboardEvent): boolean {
    return (event.ctrlKey || event.metaKey) &&
      event.altKey === false &&
      event.key.toLowerCase() === "e";
  }

  private notifyWhyCriticalPointCannotBeSaved(): void {
    if (this.formGroupForCriticalPoint == null) {
      this.notifierService.notify(
        "error",
        "A kritikus pont még nem menthető, próbáld újra egy pillanat múlva."
      );
      return;
    }

    if (this.hasUserPracticePathWritePermission() === false) {
      this.notifierService.notify(
        "error",
        "A kritikus pont mentése nem lehetséges, mert nincs szerkesztési jogosultságod."
      );
      return;
    }

    this.formGroupForCriticalPoint.markAllAsTouched();

    const titleControl = this.formGroupForCriticalPoint.get("titleFC");
    if (titleControl?.hasError("required") || titleControl?.hasError("minlength")) {
      this.notifierService.notify("error", "A kritikus pont címe kötelező.");
      return;
    }

    if (titleControl?.hasError("maxlength")) {
      this.notifierService.notify(
        "error",
        `A kritikus pont címe legfeljebb ${this.maxTitleLength} karakter lehet.`
      );
      return;
    }

    const descriptionControl = this.formGroupForCriticalPoint.get("descriptionFC");
    if (descriptionControl?.hasError("required") || descriptionControl?.hasError("minlength")) {
      this.notifierService.notify("error", "A kritikus pont leírása kötelező.");
      return;
    }

    if (descriptionControl?.hasError("noNewlineCharacter")) {
      this.notifierService.notify(
        "error",
        "A kritikus pont leírása nem tartalmazhat sortörést."
      );
      return;
    }

    this.notifierService.notify(
      "error",
      "A kritikus pont jelenleg nem menthető. Ellenőrizd a kötelező mezőket."
    );
  }

  private emitNavigationCommand(direction: CriticalPointEditorNavigationDirection): void {
    const navigationCommand: CriticalPointEditorNavigationCommand = {
      type: "navigate",
      direction,
    };
    this.dialog.close(navigationCommand);
  }

  private clampStopPointFocusCenters(): void {
    this.data.stopPoints.forEach((stopPoint) => {
      if (stopPoint.focusSettings == undefined) {
        return;
      }

      stopPoint.focusSettings.x = this.clampFocusCenterX(
        stopPoint.focusSettings.x
      );
    });
  }

  private clampFocusCenterX(relativeX: number): number {
    return Math.max(
      this.focusHorizontalPadding,
      Math.min(1 - this.focusHorizontalPadding, relativeX)
    );
  }

  public getSnapshot = async (): Promise<string | undefined> => {
    if (
      !this.videoPlayerComponent ||
      !this.videoPlayerComponent.nativeHtmlVideoElement
    ) {
      return undefined;
    }

    const video = this.videoPlayerComponent.nativeHtmlVideoElement;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return undefined;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg").split(",")[1];
  }
}
